import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import TextInput from '~/components/Form/TextInput';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';
import { isInAppBrowser } from '~/utils/browser';

import LoginButtons from '../../../LoginButtons';
import Tracked from '../../../Tracked';
import { authButtonClassName, authInputClassName } from '../AuthDialog';
import AuthPanel from '../AuthPanel';
import { type ScreenProps, Stage } from './';
import OtherLoginOptionsMessage from './OtherLoginOptionsMessage';

function AuthenticationErrorMessage({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  if (!isActive) return null;

  return (
    <div className="flex flex-row items-center justify-start gap-2">
      <div className="shrink-0">
        <ErrorIcon />
      </div>
      <p className="text-sm font-semibold text-red-alert">
        {t('login.stageSignIn.incorrectDetails')}
      </p>
    </div>
  );
}

export default function SignInScreen({
  onDismiss,
  postAuthOptions,
  setStage,
  onError
}: ScreenProps) {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const { t } = useTranslation();

  const errors = fetcher.data?.errors;
  const idle = fetcher.state === 'idle';

  const onSignIn = useCallback(
    (loginType: LoginSource) => () => {
      gtm.sendLoginEvent(user.id, loginType);

      braze.setUser(user.id);
      braze.logLoginEvent();

      onDismiss({ loginSuccessful: true });
    },
    [user.id, onDismiss]
  );

  useEffect(() => {
    if (fetcher.data?.success === true) onSignIn(LoginSource.Email)();
  }, [fetcher.data?.success, onSignIn]);

  const isInApp = isInAppBrowser(navigator.userAgent);

  return (
    <>
      <fetcher.Form
        action={`/bolt/login/email${postAuthOptions?.sizes ? '?sizes=' + JSON.stringify(postAuthOptions.sizes) : ''}`}
        className="w-full"
        method="post"
      >
        <input
          type="hidden"
          value={postAuthOptions?.productId}
          name="productId"
        />
        <input
          type="hidden"
          value={postAuthOptions?.redirectUrl}
          name="redirectUrl"
        />
        <input type="hidden" value={user.guestId} name="guestId" />
        <input type="hidden" value={postAuthOptions?.action} name="action" />
        <AuthPanel className="gap-4 px-4 py-2 pt-4">
          <TextInput
            backgroundTheme="light"
            className={authInputClassName}
            error={errors?.email ? t('login.emailFormatError') : undefined}
            id="login-email"
            name="email"
            placeholder={t('login.emailAddress')}
            type="email"
          />
          <TextInput
            backgroundTheme="light"
            className={authInputClassName}
            error={
              errors?.password ? t('login.passwordLengthError') : undefined
            }
            id="login-password"
            name="password"
            placeholder={t('login.yourPassword')}
            type="password"
          />
          <Tracked.Button
            className={authButtonClassName}
            variant={idle ? 'forest' : 'disabled'}
            disabled={!idle}
            name="SIGN_IN_EMAIL"
            pagePosition={'Sign in modal'}
          >
            {idle ? t('login.signInButton') : t('login.signingIn')}
          </Tracked.Button>
          <AuthenticationErrorMessage
            isActive={Boolean(fetcher?.data?.errors?.authentication)}
          />
          <button
            onClick={() => setStage(Stage.ForgotPass)}
            type="button"
            className="font-heading text-xs font-medium leading-6 tracking-[-0.02em]"
          >
            {t('login.forgotPassword')}
          </button>
        </AuthPanel>
      </fetcher.Form>

      <AuthPanel className="gap-2 px-4 pb-4 pt-2">
        {isInApp ? (
          <OtherLoginOptionsMessage />
        ) : (
          <>
            <p className="font-heading text-xs font-medium leading-6 tracking-[-0.02em]">
              {t('login.stageSignIn.orSignInWith')}
            </p>
            <div className="grid w-full grid-cols-3 gap-4">
              <LoginButtons.Apple
                appearance="compact"
                pagePosition="Sign in modal"
                postAuthOptions={postAuthOptions}
                onSuccess={onSignIn(LoginSource.Apple)}
                onError={onError}
              />
              <LoginButtons.Google
                appearance="compact"
                pagePosition="Sign in modal"
                postAuthOptions={postAuthOptions}
                onSuccess={onSignIn(LoginSource.Google)}
                onError={onError}
              />
              <LoginButtons.Facebook
                appearance="compact"
                pagePosition="Sign in modal"
                postAuthOptions={postAuthOptions}
                onSuccess={onSignIn(LoginSource.Meta)}
                onError={onError}
              />
            </div>
          </>
        )}
      </AuthPanel>
    </>
  );
}
